
import { html, LitElement } from 'lit';
import styles from './key-value-list.scss';
import { customElement, property } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map.js';

/**
 * Key–value list rendered using a grid layout.
 *
 * This component accepts two types of children:
 *  - a direct `dl` element
 *  - a `usos-frame-section` element containing a `dl` inside
 *
 * @slot - kv-list content
 */
@customElement('kv-list')
export class KeyValueList extends LitElement {
    static override styles = styles;


    /**
     * Enables compact width mode.
     * In this mode the key column is constrained between its min-content width
     * and a maximum of 25% of the available space.
     */
    @property({type: Boolean, reflect: true}) compact = false;

    /**
     * Enables fit-label mode.
     * The key column may expand up to 50% of the available width
     * to keep the label and value on a single line when possible.
     */
    @property({type: Boolean, reflect: true, attribute: 'fit-label'})
        fitLabel = false;

    /**
     * Enables fixed-width mode.
     * The attribute value (1–99, default 25) specifies the percentage width
     * allocated to the key column.
     */
    @property({
        reflect: true,
        converter: {
            fromAttribute: (value: string) => {
                if (value === '') return null;
                const n = parseInt(value);
                return n >= 1 && n <= 99 ? n>>0 : 25
            }
        }
    }) fixed: Optional<number> = null;


    static supportsSubgrid = CSS.supports('grid-template-columns', 'subgrid');


    override render() {
        const fixedStyle = this.fixed != null ? styleMap({
            '--kv-grid-first-column': `${this.fixed}%`
        }) : styleMap({});

        return html`
            <div style=${fixedStyle}>
                <slot @slotchange=${this._onSlotChange}></slot>
            </div>`;
    }

    protected override updated(): void {
        const modes = [
            this.compact,
            this.fitLabel,
            this.fixed !== null
        ];

        const enabled = modes.filter(Boolean).length;

        if (enabled > 1) {
            console.warn('KeyValueList has conflicting modes', this);
        } else if (enabled == 0) {
            this.compact = true;
        }
    }


    private _onSlotChange () {
        this.querySelectorAll('usos-frame-section').forEach(section => {
            section.subgrid = true;
        });
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "kv-list": KeyValueList,
    }
}
